import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { change, getFormValues } from 'redux-form';

import { Icon } from 'rhombus-react';

import { pluralize } from 'utils/common';
import ProgressContainer from 'widgets/ProgressContainer';

import { AI_GENERATE_VERSIONS_FORM } from "./constants";
import VariableRow from "./VariableRow";

import "./VariablesSelector.css";

const MAX_IMAGES = 1;
const MAX_VARIABLES = 5;
const SELECT_TYPES = ["Image", "Text"];

function canSelectVariable(variable, totalText, totalImage){
	let { type } = variable;

	if(!SELECT_TYPES.includes(type)){
		return false;
	}

	if(type === "Image"){
		if(totalImage >= MAX_IMAGES){
			return false;
		}

		if(!variable.cropMetadata){
			return false;
		}
		if(!variable.cropMetadata?.frame?.width){
			return false;
		}
	}
	if((totalText + totalImage) >= MAX_VARIABLES){
		return false;
	}

	return true;
}

function getVariableTip(variable, totalText, totalImage, currentSelected){
	let { type, name } = variable;

	let figureItOutForMe = canSelectVariable(variable, totalText, totalImage);

	// We can select it
	if(figureItOutForMe){ return null; }

	const isSelected = currentSelected[name];
	// If the current variable is selected, we should be able to deselect it
	if(isSelected){ return null; }

	if(type === "Image"){
		if(totalImage >= MAX_IMAGES){
			return `Up to ${MAX_IMAGES} image ${pluralize("variable", MAX_IMAGES)}<br />can be used at a time`;
		}
		if((totalImage + totalText) >= MAX_VARIABLES){
			return `Up to ${MAX_VARIABLES} ${pluralize("variable", MAX_VARIABLES)} can<br />be used at a time`;
		}


		// If we don't have the max number of images, than the only reason we can't select it is it's missing crop data
		return "To use this variable in AI version generation,<br />please add component dimensions to the manifest and re-upload";
	}

	return `Up to ${MAX_VARIABLES} ${pluralize("variable", MAX_VARIABLES)} can<br />be used at a time`;
}


export function initialVariableSelection(variables = []){
	let numTextSelected = 0;
	let numImagesSelected = 0;

	const initialValue = variables.reduce((prev, variable) => {
		const { name, type } = variable;

		let figureItOutForMe = canSelectVariable(variable, numTextSelected, numImagesSelected);

		if(figureItOutForMe){
			if(type === "Image"){
				numImagesSelected += 1;
			}
			if(type === "Text"){
				numTextSelected += 1;
			}
		}

		return {
			...prev,
			[name]: figureItOutForMe,
		};
	}, {});

	return initialValue;
}

function NoVariables(){
	const message = "Selected creatives don't share any dynamic variables of type text or image. Creatives used for generation must have at least one variable in common.";
	return (
		<div className="no-variables-error">
			<Icon name="far fa-bolt" />
			<p>
				{message}
			</p>
		</div>
	);
}

export default function VariablesSelector(props){
	const { anyTouched } = props;
	const dispatch = useDispatch();
	const { variables, isVariablesLoaded } = useSelector(state => state.versionGeneration || {});
	const formValues = useSelector(getFormValues(AI_GENERATE_VERSIONS_FORM));

	const selected = formValues.variables || {};
	const selectedVariables = variables.filter(variable => selected[variable.name]);
	const selectedImageVariables = selectedVariables.filter(variable => variable.type === "Image");
	const selectedTextVariables = selectedVariables.filter(variable => variable.type === "Text");


	useEffect(() => {
		if (!anyTouched) {
			dispatch(change(AI_GENERATE_VERSIONS_FORM, "variables", initialVariableSelection(variables)));
		}
	}, [variables, anyTouched]);

	let content;

	if(variables?.length){
		content = variables?.map?.((variable) => {
			let tip = getVariableTip(variable, selectedTextVariables.length, selectedImageVariables.length, selected)
				, isDisabled = !!tip
				;

			return (
				<VariableRow
					key={variable.name}
					variable={variable}
					isDisabled={isDisabled}
					tip={tip}
				/>
			);
		});
	} else {
		content = <NoVariables />;
	}

	return (
		<div className="variables-selector">
			<h2>Variables to Generate</h2>
			<ProgressContainer isLoading={!isVariablesLoaded}>
				{content}
			</ProgressContainer>
		</div>
	);
}

VariablesSelector.displayName = "VariablesSelector";
